import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { logger } from "../../observability/LoggerFactory";
import { useState, useCallback, useRef } from "react";
import type { paths } from "../../api/generated/api-types";
import type { PostSseClient } from "../../api/PostSseClient";
import { createPostSseClient } from "../../api/factory/CreatePostSseClient";

type ValidateRequest =
  paths["/proxies/import/validate"]["post"]["requestBody"]["content"]["application/json"];

type SaveRequest =
  paths["/proxies/import/save"]["post"]["requestBody"]["content"]["application/json"];

type SaveResponse =
  paths["/proxies/import/save"]["post"]["responses"]["200"]["content"]["application/json"];

export interface ValidatedProxyEntry {
  host: string;
  port: number;
  login: string;
  password: string;
  status: string;
  errorCode: string | null;
  isDuplicate: boolean;
}

export interface ProxyValidationSummary {
  totalCount: number;
  activeCount: number;
  inactiveCount: number;
  duplicateCount: number;
  parseErrorCount: number;
}

type ProxyImportState =
  | { kind: "form" }
  | { kind: "validating"; entries: ValidatedProxyEntry[] }
  | {
      kind: "results";
      entries: ValidatedProxyEntry[];
      summary: ProxyValidationSummary;
    }
  | { kind: "saving" }
  | { kind: "success"; result: SaveResponse }
  | { kind: "error"; message: string };

export interface UseProxyImportResult {
  state: ProxyImportState;
  submitValidation: (rawData: string) => void;
  confirmSave: () => void;
  reset: () => void;
}

export function useProxyImport(): UseProxyImportResult {
  const [state, setState] = useState<ProxyImportState>({ kind: "form" });
  const clientRef = useRef<PostSseClient | null>(null);
  const entriesRef = useRef<ValidatedProxyEntry[]>([]);

  const submitValidation = useCallback((rawData: string) => {
    if (clientRef.current !== null) {
      clientRef.current.close();
    }
    entriesRef.current = [];

    setState({ kind: "validating", entries: [] });

    const body: ValidateRequest = { rawData };

    clientRef.current = createPostSseClient({
      url: "/api/proxies/import/validate",
      body,
      events: {
        "entry:validated": (data) => {
          const entry = data as ValidatedProxyEntry;
          entriesRef.current = [...entriesRef.current, entry];
          setState((previous) => {
            let next = previous;
            if (previous.kind === "validating") {
              next = { ...previous, entries: entriesRef.current };
            }
            return next;
          });
        },
        "validation:completed": (data) => {
          const summary = data as ProxyValidationSummary;
          setState({
            kind: "results",
            entries: entriesRef.current,
            summary,
          });
          clientRef.current = null;
        },
      },
      onError: (error) => {
        setState({ kind: "error", message: error.message });
        clientRef.current = null;
      },
    });
  }, []);

  const confirmSave = useCallback(() => {
    void (async () => {
      if (state.kind !== "results") {
        return;
      }

      const nonDuplicateEntries = state.entries.filter((event) => !event.isDuplicate);

      setState({ kind: "saving" });

      try {
        const body: SaveRequest = {
          entries: nonDuplicateEntries.map((event) => ({
            host: event.host,
            port: event.port,
            login: event.login,
            password: event.password,
            status: event.status as SaveRequest["entries"][number]["status"],
            errorCode: event.errorCode,
          })),
        };

        const response = await fetchWithRefresh("/api/proxies/import/save", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const saved = (await response.json()) as SaveResponse;
        setState({ kind: "success", result: saved });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        logger.error({ err: message, component: "useProxyImport" }, "Failed to save imported proxies");
        setState({ kind: "error", message });
      }
    })();
  }, [state]);

  const reset = useCallback(() => {
    if (clientRef.current !== null) {
      clientRef.current.close();
    }
    clientRef.current = null;
    entriesRef.current = [];
    setState({ kind: "form" });
  }, []);

  return { state, submitValidation, confirmSave, reset };
}
